/**
 * Hormone Health Assessment — content + analytics helpers
 *
 * Kept here (single source of truth) so the Intake page can import
 * sections and clusters without re-defining them. Edit copy here.
 */

import { Zap, Scale, Brain, Heart, FlaskConical } from 'lucide-react';

// One-time tracking helper (Meta Pixel + Google Ads)
const EVENT_FIRED_PREFIX = 'glow_event_fired_';

export const fireTrackingEvent = (eventName, params = {}, forcefire = false) => {
  if (typeof window === 'undefined') return;
  const sessionKey = `${EVENT_FIRED_PREFIX}${eventName}`;
  if (!forcefire && sessionStorage.getItem(sessionKey)) return;

  if (window.fbq) {
    const standardEvents = ['PageView', 'ViewContent', 'Lead', 'CompleteRegistration', 'InitiateCheckout', 'Purchase'];
    if (standardEvents.includes(eventName)) {
      window.fbq('track', eventName, params);
    } else {
      window.fbq('trackCustom', eventName, params);
    }
  }
  if (window.gtag) {
    window.gtag('event', eventName.toLowerCase().replace(/[^a-z0-9_]/g, '_'), params);
  }
  sessionStorage.setItem(sessionKey, 'true');
};

// Symptom cluster definitions for results summary
export const SYMPTOM_CLUSTERS = {
  energy: {
    title: 'Energy & Fatigue',
    icon: Zap,
    description: 'Low energy and fatigue are among the most common signs of hormone imbalance. Many women experience these symptoms years before traditional labs show clear abnormalities.',
    pattern: 'These symptoms often relate to thyroid function, cortisol levels, or sex hormone imbalances.'
  },
  weight: {
    title: 'Weight & Metabolism',
    icon: Scale,
    description: 'Unexplained weight changes, especially around the midsection, can signal shifting hormones that affect how your body processes and stores energy.',
    pattern: 'Insulin resistance, thyroid imbalances, and declining estrogen can all contribute to metabolic changes.'
  },
  mood: {
    title: 'Mood & Cognitive',
    icon: Brain,
    description: 'Brain fog, mood swings, and sleep disruptions are frequently linked to hormone fluctuations that affect neurotransmitter balance.',
    pattern: 'Progesterone, estrogen, and cortisol all play key roles in mood regulation and cognitive clarity.'
  },
  cycle: {
    title: 'Cycle & Menopause',
    icon: Heart,
    description: 'Changes in your menstrual cycle, hot flashes, and intimacy concerns often signal the natural hormonal shifts of perimenopause or menopause.',
    pattern: 'These transitions typically begin in the late 30s to early 40s and can span 10+ years.'
  },
  history: {
    title: 'Previous Care',
    icon: FlaskConical,
    description: 'Your health history and previous testing experiences help us understand what approach may work best for you.',
    pattern: 'Many women have been told their labs are "normal" despite experiencing real symptoms.'
  }
};

// Assessment sections
export const ASSESSMENT_SECTIONS = [
  {
    id: 'energy',
    title: 'Energy & Fatigue',
    clusterId: 'energy',
    questions: [
      {
        id: 'energy_symptoms',
        type: 'multi-select',
        question: 'Which of the following have you experienced in the past 6 months?',
        subtext: 'Select all that apply',
        options: [
          { value: 'morning_fatigue', label: 'Difficulty waking up or morning fatigue' },
          { value: 'afternoon_crash', label: 'Afternoon energy crashes' },
          { value: 'exhaustion', label: 'Feeling exhausted despite adequate sleep' },
          { value: 'low_motivation', label: 'Low motivation or drive' },
          { value: 'need_caffeine', label: 'Relying on caffeine to get through the day' },
          { value: 'none', label: 'None of these' }
        ]
      }
    ]
  },
  {
    id: 'weight',
    title: 'Weight & Metabolism',
    clusterId: 'weight',
    questions: [
      {
        id: 'weight_symptoms',
        type: 'multi-select',
        question: 'Have you noticed any of the following changes?',
        subtext: 'Select all that apply',
        options: [
          { value: 'weight_gain', label: 'Unexplained weight gain' },
          { value: 'weight_loss_difficulty', label: 'Difficulty losing weight despite effort' },
          { value: 'belly_fat', label: 'Weight gain around the midsection' },
          { value: 'appetite_changes', label: 'Changes in appetite or cravings' },
          { value: 'bloating', label: 'Bloating or water retention' },
          { value: 'none', label: 'None of these' }
        ]
      }
    ]
  },
  {
    id: 'mood',
    title: 'Mood, Sleep & Brain Fog',
    clusterId: 'mood',
    questions: [
      {
        id: 'mood_symptoms',
        type: 'multi-select',
        question: 'Which of the following have you experienced recently?',
        subtext: 'Select all that apply',
        options: [
          { value: 'mood_swings', label: 'Mood swings or irritability' },
          { value: 'anxiety', label: 'Increased anxiety or worry' },
          { value: 'low_mood', label: 'Feeling down or unmotivated' },
          { value: 'brain_fog', label: 'Brain fog or difficulty concentrating' },
          { value: 'memory_issues', label: 'Memory concerns' },
          { value: 'none', label: 'None of these' }
        ]
      },
      {
        id: 'sleep_symptoms',
        type: 'multi-select',
        question: 'How is your sleep?',
        subtext: 'Select all that apply',
        options: [
          { value: 'trouble_falling_asleep', label: 'Trouble falling asleep' },
          { value: 'waking_up', label: 'Waking up during the night' },
          { value: 'not_rested', label: 'Not feeling rested after sleep' },
          { value: 'night_sweats', label: 'Night sweats disrupting sleep' },
          { value: 'sleep_fine', label: 'My sleep is generally fine' }
        ]
      }
    ]
  },
  {
    id: 'life_stage',
    title: 'Life Stage',
    clusterId: null,
    questions: [
      {
        id: 'age_range',
        type: 'single-select',
        question: 'Which age range best describes you?',
        subtext: 'This helps us personalize your results',
        options: [
          { value: 'under_30', label: 'Under 30' },
          { value: '30_39', label: '30 to 39' },
          { value: '40_49', label: '40 to 49' },
          { value: '50_59', label: '50 to 59' },
          { value: '60_plus', label: '60 or older' }
        ]
      }
    ]
  },
  {
    id: 'cycle',
    title: 'Cycle & Menopause',
    clusterId: 'cycle',
    questions: [
      {
        id: 'cycle_status',
        type: 'single-select',
        question: 'Where are you in your hormonal journey?',
        options: [
          { value: 'regular_cycles', label: 'I have regular monthly cycles' },
          { value: 'irregular_cycles', label: 'My cycles have become irregular' },
          { value: 'perimenopause', label: 'I think I may be in perimenopause' },
          { value: 'menopause', label: "I haven't had a period in 12+ months" },
          { value: 'post_menopause', label: 'I am post-menopausal' },
          { value: 'unsure', label: "I'm not sure" }
        ]
      },
      {
        id: 'cycle_symptoms',
        type: 'multi-select',
        question: 'Have you experienced any of the following?',
        subtext: 'Select all that apply',
        options: [
          { value: 'hot_flashes', label: 'Hot flashes or sudden warmth' },
          { value: 'low_libido', label: 'Decreased interest in intimacy' },
          { value: 'vaginal_dryness', label: 'Vaginal dryness or discomfort' },
          { value: 'pms_changes', label: 'Changes in PMS symptoms' },
          { value: 'heavy_periods', label: 'Heavy or prolonged periods' },
          { value: 'none', label: 'None of these' }
        ]
      }
    ]
  },
  {
    id: 'history',
    title: 'Previous Labs & Treatments',
    clusterId: 'history',
    questions: [
      {
        id: 'previous_testing',
        type: 'single-select',
        question: 'Have you had hormone testing done before?',
        options: [
          { value: 'never', label: "No, I haven't had hormone testing" },
          { value: 'yes_normal', label: 'Yes, and I was told everything was "normal"' },
          { value: 'yes_abnormal', label: 'Yes, and there were some concerns' },
          { value: 'yes_treatment', label: "Yes, and I'm currently being treated" },
          { value: 'not_sure', label: "I'm not sure" }
        ]
      },
      {
        id: 'seeking_help',
        type: 'single-select',
        question: "What best describes why you're here today?",
        options: [
          { value: 'curious', label: "I'm curious if my symptoms could be hormone-related" },
          { value: 'dismissed', label: "I've been dismissed by other providers" },
          { value: 'second_opinion', label: 'I want a second opinion on my hormone health' },
          { value: 'proactive', label: 'I want to be proactive about my health' },
          { value: 'treatment', label: "I'm looking for treatment options" }
        ]
      }
    ]
  },
  {
    id: 'next_step',
    title: 'Final Question',
    clusterId: null,
    introText: 'Your responses suggest that hormonal imbalance may be contributing to your symptoms. Many patients see meaningful improvement once properly evaluated and treated.',
    questions: [
      {
        id: 'next_step_intent',
        type: 'single-select',
        question: 'Would you like to speak with a provider about next steps?',
        options: [
          { value: 'yes_speak', label: "Yes, I'd like to speak with a provider" },
          { value: 'open_learning', label: "I'm open to learning more" },
          { value: 'not_now', label: 'Not at this time' }
        ]
      }
    ]
  }
];

// Build the relevant clusters list from raw answers
export const getRelevantClusters = (answers) => {
  const clusters = [];

  const energyAnswers = answers.energy_symptoms || [];
  if (energyAnswers.length > 0 && !energyAnswers.includes('none')) {
    clusters.push({ ...SYMPTOM_CLUSTERS.energy, symptoms: energyAnswers });
  }

  const weightAnswers = answers.weight_symptoms || [];
  if (weightAnswers.length > 0 && !weightAnswers.includes('none')) {
    clusters.push({ ...SYMPTOM_CLUSTERS.weight, symptoms: weightAnswers });
  }

  const moodAnswers = answers.mood_symptoms || [];
  const sleepAnswers = answers.sleep_symptoms || [];
  if ((moodAnswers.length > 0 && !moodAnswers.includes('none')) ||
      (sleepAnswers.length > 0 && !sleepAnswers.includes('sleep_fine'))) {
    clusters.push({ ...SYMPTOM_CLUSTERS.mood, symptoms: [...moodAnswers, ...sleepAnswers] });
  }

  const cycleStatus = answers.cycle_status;
  const cycleSymptoms = answers.cycle_symptoms || [];
  if (cycleStatus && cycleStatus !== 'regular_cycles') {
    clusters.push({ ...SYMPTOM_CLUSTERS.cycle, symptoms: cycleSymptoms });
  } else if (cycleSymptoms.length > 0 && !cycleSymptoms.includes('none')) {
    clusters.push({ ...SYMPTOM_CLUSTERS.cycle, symptoms: cycleSymptoms });
  }

  return clusters;
};
